import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Grid, Chip, Button, Divider, IconButton,
  Stack, List, ListItem, ListItemText, CircularProgress, Tooltip, Alert,
  TextField, LinearProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  MenuItem, Autocomplete, FormControl, InputLabel, Select,
} from '@mui/material';
import {
  ArrowBack, Edit, OpenInNew, Description, CloudUpload, Delete, Add,
  AttachFile, Group, Save, EmojiEvents,
} from '@mui/icons-material';
import api from '../../services/api';

const STATUS_LABEL = {
  open: 'Open', researching: 'Researching', suppliers_identified: 'Suppliers Identified',
  quotes_received: 'Quotes Received', supplier_selected: 'Supplier Selected',
  closed: 'Closed', cancelled: 'Cancelled',
};
const STATUS_COLOR = {
  open: 'default', researching: 'info', suppliers_identified: 'primary',
  quotes_received: 'warning', supplier_selected: 'success',
  closed: 'secondary', cancelled: 'error',
};
const PRIORITY_COLOR = { urgent: 'error', high: 'warning', medium: 'primary', low: 'default' };
const CAND_STATUS = [
  { v: 'shortlisted', label: 'Shortlisted', color: 'default' },
  { v: 'contacted',   label: 'Contacted',   color: 'info' },
  { v: 'quoted',      label: 'Quoted',      color: 'primary' },
  { v: 'rejected',    label: 'Rejected',    color: 'error' },
  { v: 'selected',    label: 'Selected',    color: 'success' },
];

const formatBytes = (n) => !n ? '—' : n < 1024 ? `${n} B` : n < 1024*1024 ? `${(n/1024).toFixed(1)} KB` : `${(n/(1024*1024)).toFixed(2)} MB`;

const qty = (v) => {
  if (v == null || v === '') return '—';
  const n = parseFloat(v);
  if (isNaN(n)) return '—';
  return Number.isInteger(n) ? n.toLocaleString('en-US') : n.toLocaleString('en-US', { maximumFractionDigits: 2 });
};

const Field = ({ label, value }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.5 }}>{label}</Typography>
    <Typography variant="body2" sx={{ mt: 0.3, whiteSpace: 'pre-wrap' }}>{value || '—'}</Typography>
  </Box>
);

export default function ProductSearchDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [uploading, setUploading] = useState(false);
  const [fileDescription, setFileDescription] = useState('');

  // Candidate dialog
  const [candDialog, setCandDialog] = useState(false);
  const [candDraft, setCandDraft] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [beneficiaries, setBeneficiaries] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, c, f] = await Promise.all([
        api.get(`/sourcing-requests/${id}`),
        api.get(`/sourcing-requests/${id}/candidates`),
        api.get(`/sourcing-requests/${id}/files`),
      ]);
      setRequest(r.data.data);
      setCandidates(c.data.data || []);
      setFiles(f.data.data || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load');
    }
    setLoading(false);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  // Lookup data for candidate dialog
  useEffect(() => {
    if (!candDialog) return;
    api.get('/sourcing-suppliers').then(r => setSuppliers(r.data.data || []));
    api.get('/beneficiaries').then(r => setBeneficiaries(r.data.data || []));
  }, [candDialog]);

  // ── Files ────────────────────────────────────────────────────────────────
  const openFile = async (fileId) => {
    try {
      const res = await api.get(`/sourcing-requests/files/${fileId}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { alert('Unable to open file'); }
  };
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    if (fileDescription) fd.append('description', fileDescription);
    try {
      await api.post(`/sourcing-requests/${id}/files`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const f = await api.get(`/sourcing-requests/${id}/files`);
      setFiles(f.data.data || []);
      setFileDescription('');
    } catch (err) { alert(err.response?.data?.message || 'Upload failed'); }
    setUploading(false);
    e.target.value = '';
  };
  const deleteFile = async (f) => {
    if (!window.confirm(`Remove "${f.original_name}"?`)) return;
    await api.delete(`/sourcing-requests/files/${f.id}`);
    setFiles(files.filter(x => x.id !== f.id));
  };

  // ── Candidates ───────────────────────────────────────────────────────────
  const openCand = (c) => {
    setCandDraft(c ? { ...c } : {
      sourcing_supplier_id: '', beneficiary_id: '', external_supplier_name: '',
      status: 'shortlisted', quoted_unit_price: '', quoted_currency: request?.target_currency || 'USD',
      quoted_moq: '', lead_time_days: '', payment_terms: '', notes: '',
    });
    setCandDialog(true);
  };
  const saveCand = async () => {
    const payload = { ...candDraft };
    ['sourcing_supplier_id','beneficiary_id','quoted_unit_price','quoted_moq','lead_time_days'].forEach(k => {
      if (payload[k] === '' || payload[k] == null) payload[k] = null;
      else if (k.includes('id') || k === 'lead_time_days') payload[k] = parseInt(payload[k]);
      else payload[k] = parseFloat(payload[k]);
    });
    try {
      if (candDraft.id) await api.put(`/sourcing-requests/candidates/${candDraft.id}`, payload);
      else              await api.post(`/sourcing-requests/${id}/candidates`, payload);
      setCandDialog(false);
      const c = await api.get(`/sourcing-requests/${id}/candidates`);
      setCandidates(c.data.data || []);
    } catch (err) { alert(err.response?.data?.message || 'Failed'); }
  };
  const deleteCand = async (c) => {
    if (!window.confirm('Remove this candidate?')) return;
    await api.delete(`/sourcing-requests/candidates/${c.id}`);
    setCandidates(candidates.filter(x => x.id !== c.id));
  };
  const markCandSelected = async (c) => {
    await api.put(`/sourcing-requests/candidates/${c.id}`, { status: 'selected' });
    await api.put(`/sourcing-requests/${id}`, { status: 'supplier_selected' });
    load();
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  if (error) return <Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box>;
  if (!request) return null;

  const overdueDays = request.required_by && !['closed','cancelled','supplier_selected'].includes(request.status)
    ? Math.floor((new Date() - new Date(request.required_by)) / (86400 * 1000)) : null;

  const candDraftSupplier = (() => {
    if (!candDraft) return null;
    if (candDraft.sourcing_supplier_id) return { type: 'sourcing', id: candDraft.sourcing_supplier_id };
    if (candDraft.beneficiary_id) return { type: 'beneficiary', id: candDraft.beneficiary_id };
    if (candDraft.external_supplier_name) return { type: 'external' };
    return null;
  })();

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', md: 'center' }, mb: 2, gap: 1, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate('/sourcing/searches')}><ArrowBack /></IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.5, gap: 1 }}>
            <Typography variant="h6" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{request.request_ref}</Typography>
            <Chip size="small" label={STATUS_LABEL[request.status] || request.status} color={STATUS_COLOR[request.status]} />
            <Chip size="small" label={request.priority?.toUpperCase()} color={PRIORITY_COLOR[request.priority]} />
            {overdueDays > 0 && <Chip size="small" label={`${overdueDays}d overdue`} color="error" />}
          </Stack>
          <Typography variant="h6" sx={{ fontSize: { xs: '1.05rem', md: '1.25rem' }, fontWeight: 700 }}>{request.product_name}</Typography>
          {request.category && (
            <Chip size="small" label={request.category} variant="outlined" sx={{ mt: 0.5 }} />
          )}
        </Box>
        <Button variant="contained" size="small" startIcon={<Edit />}
          onClick={() => navigate(`/sourcing/searches?edit=${id}`)}>Edit</Button>
      </Box>

      <Grid container spacing={2}>
        {/* Brief */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Search Brief</Typography>
            <Field label="Description" value={request.description} />
            <Field label="Specifications" value={request.specifications} />
            <Grid container spacing={2}>
              <Grid item xs={6}><Field label="Target Quantity" value={`${qty(request.target_quantity)} ${request.unit_of_measure || ''}`} /></Grid>
              <Grid item xs={6}><Field label="Target Price" value={request.target_unit_price ? `${request.target_currency} ${request.target_unit_price}` : null} /></Grid>
              <Grid item xs={6}><Field label="Required By" value={request.required_by?.substring(0, 10)} /></Grid>
              <Grid item xs={6}><Field label="Created" value={request.created_at?.substring(0, 10)} /></Grid>
              <Grid item xs={12}><Field label="Notes" value={request.notes} /></Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Candidates table */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2.5, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Group fontSize="small" /> Candidate Suppliers ({candidates.length})
              </Typography>
              <Button size="small" variant="contained" startIcon={<Add />} onClick={() => openCand(null)}>
                Add Candidate
              </Button>
            </Box>
            {candidates.length === 0 ? (
              <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                No candidates yet. Add suppliers you're evaluating — including unknown ones (just type a name).
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Supplier</TableCell>
                      <TableCell align="right">Quote</TableCell>
                      <TableCell align="right">MOQ</TableCell>
                      <TableCell align="right">Lead</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {candidates.map(c => {
                      const isExt = !c.sourcing_supplier_id && !c.beneficiary_id;
                      const name = c.beneficiary_name || c.sourcing_supplier_name || c.external_supplier_name;
                      const opt = CAND_STATUS.find(x => x.v === c.status);
                      return (
                        <TableRow key={c.id} hover sx={{ bgcolor: c.status === 'selected' ? 'success.50' : undefined }}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>{name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {c.beneficiary_id ? 'Active beneficiary' : c.sourcing_supplier_id ? `Sourcing candidate · ${c.sourcing_country || ''}` : 'External (not in system)'}
                            </Typography>
                            {c.notes && (
                              <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic', color: 'text.secondary', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {c.notes}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">{c.quoted_unit_price ? `${c.quoted_currency || ''} ${c.quoted_unit_price}` : '—'}</TableCell>
                          <TableCell align="right">{qty(c.quoted_moq)}</TableCell>
                          <TableCell align="right">{c.lead_time_days ? `${c.lead_time_days} d` : '—'}</TableCell>
                          <TableCell>
                            <Chip size="small" label={opt?.label || c.status} color={opt?.color}
                              variant={opt?.color === 'default' ? 'outlined' : 'filled'} />
                          </TableCell>
                          <TableCell align="right">
                            {c.status !== 'selected' && (
                              <Tooltip title="Mark as Selected"><IconButton size="small" color="success" onClick={() => markCandSelected(c)}><EmojiEvents fontSize="small" /></IconButton></Tooltip>
                            )}
                            <IconButton size="small" onClick={() => openCand(c)}><Edit fontSize="small" /></IconButton>
                            <IconButton size="small" color="error" onClick={() => deleteCand(c)}><Delete fontSize="small" /></IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          {/* Files */}
          <Paper sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AttachFile fontSize="small" /> Files ({files.length})
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
                <TextField size="small" label="Description (optional)"
                  placeholder="Spec sheet, sample photo, reference design…"
                  value={fileDescription} onChange={e => setFileDescription(e.target.value)} sx={{ flex: 1 }} />
                <Button component="label" variant="contained" startIcon={<CloudUpload />} disabled={uploading}>
                  {uploading ? 'Uploading…' : 'Upload File'}
                  <input type="file" hidden onChange={handleUpload} />
                </Button>
              </Stack>
              {uploading && <LinearProgress sx={{ mt: 1 }} />}
            </Paper>
            {files.length === 0 ? (
              <Typography color="text.secondary" align="center" sx={{ py: 3 }}>No files uploaded yet.</Typography>
            ) : (
              <List dense>
                {files.map(f => (
                  <ListItem key={f.id} divider sx={{ pl: 0 }}
                    secondaryAction={
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Open"><IconButton size="small" onClick={() => openFile(f.id)}><OpenInNew fontSize="small" /></IconButton></Tooltip>
                        <IconButton size="small" color="error" onClick={() => deleteFile(f)}><Delete fontSize="small" /></IconButton>
                      </Stack>
                    }>
                    <Description sx={{ mr: 1.5, color: 'action.active' }} />
                    <ListItemText
                      primary={<Box component="span" onClick={() => openFile(f.id)}
                        sx={{ color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                        {f.original_name}
                      </Box>}
                      secondary={`${f.description ? f.description + ' · ' : ''}${formatBytes(f.size_bytes)} · ${f.uploaded_by_name || 'Unknown'}`}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Candidate dialog */}
      <Dialog open={candDialog}
        onClose={(_, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') setCandDialog(false); }}
        fullWidth maxWidth="sm">
        <DialogTitle>{candDraft?.id ? 'Edit Candidate' : 'Add Candidate Supplier'}</DialogTitle>
        <DialogContent dividers>
          {candDraft && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Pick the supplier (one of three options):</Typography>
              </Grid>
              <Grid item xs={12}>
                <Autocomplete size="small"
                  options={suppliers}
                  getOptionLabel={(o) => o.company_name || ''}
                  value={suppliers.find(s => s.id === parseInt(candDraft.sourcing_supplier_id)) || null}
                  onChange={(_, v) => setCandDraft({ ...candDraft,
                    sourcing_supplier_id: v?.id || '', beneficiary_id: v ? '' : candDraft.beneficiary_id,
                    external_supplier_name: v ? '' : candDraft.external_supplier_name })}
                  renderInput={(params) => <TextField {...params} label="Sourcing candidate (under discovery)" />}
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete size="small"
                  options={beneficiaries}
                  getOptionLabel={(o) => o.name || ''}
                  value={beneficiaries.find(b => b.id === parseInt(candDraft.beneficiary_id)) || null}
                  onChange={(_, v) => setCandDraft({ ...candDraft,
                    beneficiary_id: v?.id || '', sourcing_supplier_id: v ? '' : candDraft.sourcing_supplier_id,
                    external_supplier_name: v ? '' : candDraft.external_supplier_name })}
                  renderInput={(params) => <TextField {...params} label="Existing beneficiary (active supplier)" />}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField size="small" fullWidth label="External supplier name (free text)"
                  placeholder="If you only have a name — e.g. 'Yiwu factory'"
                  value={candDraft.external_supplier_name || ''}
                  onChange={e => setCandDraft({ ...candDraft,
                    external_supplier_name: e.target.value,
                    sourcing_supplier_id: e.target.value ? '' : candDraft.sourcing_supplier_id,
                    beneficiary_id: e.target.value ? '' : candDraft.beneficiary_id })}
                />
              </Grid>
              <Grid item xs={12}><Divider><Typography variant="caption" color="text.secondary">QUOTE</Typography></Divider></Grid>
              <Grid item xs={6}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select value={candDraft.status} label="Status"
                    onChange={e => setCandDraft({ ...candDraft, status: e.target.value })}>
                    {CAND_STATUS.map(s => <MenuItem key={s.v} value={s.v}>{s.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}><TextField size="small" fullWidth type="number" label="Lead Time (days)"
                value={candDraft.lead_time_days || ''}
                onChange={e => setCandDraft({ ...candDraft, lead_time_days: e.target.value })} /></Grid>
              <Grid item xs={4}><TextField size="small" fullWidth type="number" label="Quoted Unit Price"
                value={candDraft.quoted_unit_price || ''}
                onChange={e => setCandDraft({ ...candDraft, quoted_unit_price: e.target.value })} /></Grid>
              <Grid item xs={4}>
                <TextField size="small" select fullWidth label="Currency"
                  value={candDraft.quoted_currency || 'USD'}
                  onChange={e => setCandDraft({ ...candDraft, quoted_currency: e.target.value })}>
                  {['USD','EUR','CNY','HKD'].map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={4}><TextField size="small" fullWidth type="number" label="MOQ"
                value={candDraft.quoted_moq || ''}
                onChange={e => setCandDraft({ ...candDraft, quoted_moq: e.target.value })} /></Grid>
              <Grid item xs={12}><TextField size="small" fullWidth label="Payment Terms"
                placeholder="30% T/T advance, 70% before shipment"
                value={candDraft.payment_terms || ''}
                onChange={e => setCandDraft({ ...candDraft, payment_terms: e.target.value })} /></Grid>
              <Grid item xs={12}><TextField size="small" fullWidth multiline rows={2} label="Notes"
                value={candDraft.notes || ''}
                onChange={e => setCandDraft({ ...candDraft, notes: e.target.value })} /></Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCandDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveCand}>{candDraft?.id ? 'Update' : 'Add'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
